import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  PAYMENT_METHOD_LABELS,
  useGetAllowedPaymentMethodsConfig,
} from '~entities/payment/api/get-allowed-payment-methods';
import { useGetEnrollmentPaymentMethods, EnrollmentPaymentProvider } from '~entities/payment/api/get-enrollment-payment-methods';
import { useTransactionFeesSettings } from '~entities/payment/api/fetch-transaction-fees-settings';
import { ENABLE_LINK_WHILE_PASS_ALONG_NOTICE } from '~entities/payment/constants/transaction-fee';
import { PaymentMethod } from '~entities/wallet/model/types';
import { Button } from '~shared/shadcn/ui/button';
import { useGetCurrency } from '~shared/api/currency-api';
import { Switch } from '~shared/shadcn/ui/switch';
import {
  useUpdateAllowedPaymentMethods,
  useUpdateAllowedPaymentMethodsMode,
  type PaymentMethodsConfig,
} from '~entities/payment/api/update-allowed-payment-methods';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';

const MODE_UPDATE_SUCCESS_MESSAGE = 'Payment method mode updated.';
const MODE_UPDATE_ERROR_MESSAGE =
  'Failed to update payment method mode. Please try again.';
const PAYMENT_METHODS_UPDATE_SUCCESS_MESSAGE =
  'Payment methods updated successfully.';
const PAYMENT_METHODS_UPDATE_ERROR_MESSAGE =
  'Failed to update payment methods. Please try again.';

const PAYMENT_METHODS_STATIC_LIST: Array<{
  id: PaymentMethod;
  label: string;
  description?: string;
}> = [
  {
    id: PaymentMethod.CARD,
    label: PAYMENT_METHOD_LABELS[PaymentMethod.CARD].short,
  },
  {
    id: PaymentMethod.LINK,
    label: PAYMENT_METHOD_LABELS[PaymentMethod.LINK].short,
    description:
      'One-click checkout that saves payment info across Stripe merchants',
  },
  {
    id: PaymentMethod.US_BANK_ACCOUNT,
    label: PAYMENT_METHOD_LABELS[PaymentMethod.US_BANK_ACCOUNT].short,
  },
  {
    id: PaymentMethod.AU_BECS_DEBIT,
    label: PAYMENT_METHOD_LABELS[PaymentMethod.AU_BECS_DEBIT].short,
  },
];

function normalizeCurrencyCode(currencyName: string | undefined): string {
  return currencyName?.trim().toUpperCase() ?? '';
}

enum StudioCurrencyCode {
  USD = 'USD',
  AUD = 'AUD',
}

function isPaymentMethodVisibleByCurrency(
  methodId: PaymentMethod,
  currencyName: string | undefined,
): boolean {
  const currency = normalizeCurrencyCode(currencyName);
  if (methodId === PaymentMethod.AU_BECS_DEBIT)
    return currency === StudioCurrencyCode.AUD;
  if (methodId === PaymentMethod.US_BANK_ACCOUNT)
    return currency === StudioCurrencyCode.USD;
  return true;
}

function isPaymentMethodVisible(params: {
  methodId: PaymentMethod;
  currencyName: string | undefined;
  isSquareProvider: boolean;
}): boolean {
  const { methodId, currencyName, isSquareProvider } = params;

  // Stripe Link is Stripe-only — never show for Square.
  if (methodId === PaymentMethod.LINK && isSquareProvider) {
    return false;
  }

  // Square card-on-file is card only (no ACH / BECS save).
  if (isSquareProvider) {
    return methodId === PaymentMethod.CARD;
  }

  return isPaymentMethodVisibleByCurrency(methodId, currencyName);
}

function enabledListToConfig(
  paymentMethodTypes: string[],
): PaymentMethodsConfig {
  const config: PaymentMethodsConfig = {};
  for (const { id } of PAYMENT_METHODS_STATIC_LIST) {
    config[id] = paymentMethodTypes.includes(id) ? 'on' : 'off';
  }
  return config;
}

function showApiErrorToast(error: unknown, defaultMessage: string) {
  const apiError = (error as { response?: { data?: { error?: string } } })
    ?.response?.data?.error;
  toast.error(apiError ?? defaultMessage);
}

interface AllowedPaymentMethodsSettingsProps {
  locationId: string;
  /** When true, load allow-list from V2 (studio preference / Stripe / Square). */
  isV2?: boolean;
}

export const AllowedPaymentMethodsSettings = ({
  locationId,
  isV2 = false,
}: AllowedPaymentMethodsSettingsProps) => {
  const { data: v2ConfigData, isLoading: isLoadingV2 } =
    useGetEnrollmentPaymentMethods({
      studioOnly: true,
      enabled: isV2,
    });
  const { data: v1ConfigData, isLoading: isLoadingV1 } =
    useGetAllowedPaymentMethodsConfig(isV2 ? '' : locationId);

  const isSquareStudio =
    isV2 && v2ConfigData?.paymentProvider === EnrollmentPaymentProvider.SQUARE;
  const canEditMethods = !isSquareStudio;
  const { data: currencyData } = useGetCurrency();
  const { data: transactionFeesSettings } = useTransactionFeesSettings();
  const passTransactionFeesToCustomers =
    transactionFeesSettings?.passTransactionFeesToCustomers === true;
  const updateMutation = useUpdateAllowedPaymentMethods();
  const updateModeMutation = useUpdateAllowedPaymentMethodsMode();

  const visiblePaymentMethods = PAYMENT_METHODS_STATIC_LIST.filter(({ id }) =>
    isPaymentMethodVisible({
      methodId: id,
      currencyName: currencyData?.name,
      isSquareProvider: isSquareStudio,
    }),
  );

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsConfig>(
    {},
  );
  const [bypassStripePaymentMethodConfig, setBypassStripePaymentMethodConfig] =
    useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showLinkPassAlongBlock, setShowLinkPassAlongBlock] = useState(false);

  useEffect(() => {
    if (isV2) {
      if (v2ConfigData === undefined) return;
      setPaymentMethods(enabledListToConfig(v2ConfigData.allowedTypes ?? []));
      setBypassStripePaymentMethodConfig(
        v2ConfigData.bypassStripePaymentMethodConfig === true,
      );
      setHasChanges(false);
      return;
    }

    if (v1ConfigData !== undefined) {
      setPaymentMethods(
        enabledListToConfig(v1ConfigData.paymentMethodTypes ?? []),
      );
      setBypassStripePaymentMethodConfig(
        v1ConfigData.bypassStripePaymentMethodConfig === true,
      );
      setHasChanges(false);
    }
  }, [isV2, v1ConfigData, v2ConfigData]);

  const handleToggle = (methodId: string, enabled: boolean) => {
    if (!canEditMethods) return;

    if (
      methodId === PaymentMethod.LINK &&
      enabled &&
      passTransactionFeesToCustomers
    ) {
      setShowLinkPassAlongBlock(true);
      return;
    }

    setPaymentMethods((prev) => ({
      ...prev,
      [methodId]: enabled ? 'on' : 'off',
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!canEditMethods) return;

    const paymentMethodsToSend: PaymentMethodsConfig = {};
    for (const { id } of visiblePaymentMethods) {
      paymentMethodsToSend[id] = paymentMethods[id] ?? 'off';
    }
    updateMutation.mutate(
      {
        locationId,
        // V1 supplies PMC id; V2 lets the BE resolve the default Stripe config.
        paymentMethodConfigId: isV2
          ? undefined
          : v1ConfigData?.paymentMethodConfigId,
        paymentMethods: paymentMethodsToSend,
      },
      {
        onSuccess: () => {
          toast.success(PAYMENT_METHODS_UPDATE_SUCCESS_MESSAGE);
          setHasChanges(false);
        },
        onError: (error: unknown) => {
          showApiErrorToast(error, PAYMENT_METHODS_UPDATE_ERROR_MESSAGE);
        },
      },
    );
  };

  const handleBypassToggle = (checked: boolean) => {
    if (!canEditMethods) return;

    setBypassStripePaymentMethodConfig(checked);
    updateModeMutation.mutate(
      {
        locationId,
        bypassStripePaymentMethodConfig: checked,
      },
      {
        onSuccess: () => {
          toast.success(MODE_UPDATE_SUCCESS_MESSAGE);
        },
        onError: (error: unknown) => {
          setBypassStripePaymentMethodConfig(
            isV2
              ? v2ConfigData?.bypassStripePaymentMethodConfig === true
              : v1ConfigData?.bypassStripePaymentMethodConfig === true,
          );
          showApiErrorToast(error, MODE_UPDATE_ERROR_MESSAGE);
        },
      },
    );
  };

  const isLoading = isV2 ? isLoadingV2 : isLoadingV1 || !locationId;
  if (isLoading) {
    return (
      <div className='flex h-32 items-center justify-center'>
        <Loader2 className='size-8 animate-spin' />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <h2 className='text-xl font-semibold'>Allowed Payment Methods</h2>
        <p className='text-sm text-muted-foreground'>
          {isSquareStudio
            ? 'Square card-on-file availability comes from your Square location capabilities.'
            : 'Choose which payment methods are available for your studio. Only enabled methods will appear when parents enroll or pay.'}
        </p>
      </div>

      {canEditMethods && (
        <div className='rounded-lg bg-gray-50 p-4'>
          <div className='flex items-start justify-between gap-4'>
            <div className='space-y-1'>
              <p className='text-sm font-medium'>
                Override Payment Provider Methods Configuration
              </p>
              <p className='text-xs text-muted-foreground'>
                Turn this on to override your payment provider dashboard
                settings with your custom settings below. Leave it off to keep
                using your payment provider settings.
              </p>
            </div>
            <Switch
              checked={bypassStripePaymentMethodConfig}
              onCheckedChange={handleBypassToggle}
              disabled={updateModeMutation.isPending}
            />
          </div>
        </div>
      )}

      <div className='space-y-3'>
        {visiblePaymentMethods.map(({ id, label, description }) => {
          const isEnabled = (paymentMethods[id] ?? 'off') === 'on';
          return (
            <div
              key={id}
              className={`rounded-lg p-5 transition-all duration-200 ${canEditMethods ? 'cursor-pointer hover:scale-[1.01]' : ''} ${isEnabled ? 'bg-amber-50 shadow-sm' : 'bg-gray-50 hover:bg-gray-100'}`}
            >
              <div className='flex items-center justify-between'>
                <div>
                  <span className='text-base font-medium'>{label}</span>
                  {description && (
                    <p className='mt-0.5 text-xs text-muted-foreground'>
                      {description}
                    </p>
                  )}
                </div>
                <div className='flex items-center gap-6'>
                  <div
                    role='button'
                    tabIndex={canEditMethods ? 0 : -1}
                    onClick={() => handleToggle(id, true)}
                    onKeyDown={(e) => {
                      if (!canEditMethods) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle(id, true);
                      }
                    }}
                    className={`flex items-center gap-2 ${canEditMethods ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
                  >
                    <div
                      className={`mt-0.5 flex size-5 flex-shrink-0 items-center justify-center rounded-full border ${isEnabled ? 'border-amber-400 bg-white' : 'border-gray-300'}`}
                    >
                      {isEnabled && (
                        <div className='size-3 rounded-full bg-amber-400' />
                      )}
                    </div>
                    <span className='text-sm text-gray-700'>Enabled</span>
                  </div>
                  <div
                    role='button'
                    tabIndex={canEditMethods ? 0 : -1}
                    onClick={() => handleToggle(id, false)}
                    onKeyDown={(e) => {
                      if (!canEditMethods) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle(id, false);
                      }
                    }}
                    className={`flex items-center gap-2 ${canEditMethods ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
                  >
                    <div
                      className={`mt-0.5 flex size-5 flex-shrink-0 items-center justify-center rounded-full border ${!isEnabled ? 'border-amber-400 bg-white' : 'border-gray-300'}`}
                    >
                      {!isEnabled && (
                        <div className='size-3 rounded-full bg-amber-400' />
                      )}
                    </div>
                    <span className='text-sm text-gray-700'>Disabled</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {hasChanges && canEditMethods && (
          <div className='flex justify-end'>
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className='rounded-lg bg-primary py-6'
            >
              {updateMutation.isPending && (
                <Loader2 className='mr-2 size-4 animate-spin' />
              )}
              Save Payment Methods
            </Button>
          </div>
        )}
      </div>

      <AlertDialog
        open={showLinkPassAlongBlock}
        onOpenChange={setShowLinkPassAlongBlock}
      >
        <AlertDialogContent className='max-w-md'>
          <AlertDialogHeader>
            <AlertDialogTitle>Stripe Link unavailable</AlertDialogTitle>
            <AlertDialogDescription className='text-left leading-relaxed'>
              {ENABLE_LINK_WHILE_PASS_ALONG_NOTICE}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
